import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Divider,
} from '@mui/material';
import {
  School as SchoolIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AccountBalance as FeeIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { familyAPI, studentAPI } from '../../hooks/reactQueryApi';
import { useNavigate } from 'react-router-dom';

export default function ParentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [feeSummary, setFeeSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadChildren();
  }, []);

  const loadChildren = async () => {
    try {
      setLoading(true);
      // Get students belonging to this family
      const familyId = user.profile;
      const response = await studentAPI.getAll({ family: familyId });
      const summaryResponse = familyId
        ? await familyAPI.getFeeSummary(familyId)
        : null;

      if (response.data.success) {
        setStudents(response.data.data);
      }
      if (summaryResponse?.data?.success) {
        setFeeSummary(summaryResponse.data.data?.combinedBalance || null);
      }
    } catch (error) {
      console.error('Error loading children:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '-';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Parent Portal
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Welcome back! View your children's information and progress.
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {students.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Children
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <SchoolIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {students.filter(s => s.status === 'Active').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: (feeSummary?.netBalance || 0) > 0 ? 'error.main' : 'success.main' }}>
                  <FeeIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    Rs. {Math.abs(feeSummary?.netBalance || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(feeSummary?.netBalance || 0) > 0 ? 'Due' : 'Advance/Clear'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Children List */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Your Children
      </Typography>

      {students.length === 0 ? (
        <Card sx={{ boxShadow: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <PersonIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No children registered
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {students.map((student) => (
            <Grid size={{ xs: 12, md: 6 }} key={student._id}>
              <Card
                sx={{
                  boxShadow: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s',
                  },
                }}
                onClick={() => navigate(`/parent/children/${student._id}`)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {student.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {student.studentId}
                      </Typography>
                    </Box>
                    <Chip
                      label={student.status}
                      size="small"
                      color={student.status === 'Active' ? 'success' : 'default'}
                    />
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">
                        Class
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {student.currentClass?.className || '-'}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">
                        Roll No.
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {student.rollNumber || '-'}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">
                        Age
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {calculateAge(student.dateOfBirth)} years
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">
                        Gender
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {student.gender}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
